/*
ForegroundObjectsList.js
wgbh-skydiagram-testing
astro.unl.edu
2019-06-16
*/


import * as React from 'react';

import {Button, Dialog} from '@blueprintjs/core';

import {ImagesList} from './ImagesList.js';

/*

Props (required):
	workspace
	onSelect
	selected		- may be null

*/


export class ForegroundObjectsList extends React.Component {


	constructor(props) {
		super(props);

		// Params are specific properties of the ParamsManager that use the getParams and setParams methods.
		// The paramsOfInterest object is used to fetch only the params used by this panel.
		// The propertiesOfInterest array includes the names of all params of interest, plus any additional
		//	properties of interest that are not params. It is used to determine if the panel needs to be re-rendered.
		// Examples: width is a param, but sunImage is not. Both are properties.
		this._paramsOfInterest = {};
		this._propertiesOfInterest = ['foregroundObjects'];
		for (const key in this._paramsOfInterest) {
			this._propertiesOfInterest.push(key);
		}

		// The same as above, but for ForegroundObjectManager instances.
		this._objectParamsOfInterest = {};
		this._objectPropertiesOfInterest = ['name'];
		for (const key in this._objectParamsOfInterest) {
			this._objectPropertiesOfInterest.push(key);
		}	

		// General methods:
		this.forceRender = this.forceRender.bind(this);
		this.paramsManagerChangeCallback = this.paramsManagerChangeCallback.bind(this);
		this.objectManagerChangeCallback = this.objectManagerChangeCallback.bind(this);
		this.registerObjectCallbacks = this.registerObjectCallbacks.bind(this);

		// Add new object methods:
		this.handleOpenNewObjectDialog = this.handleOpenNewObjectDialog.bind(this);
		this.handleCloseNewObjectDialog = this.handleCloseNewObjectDialog.bind(this);
		this.handleSelectImage = this.handleSelectImage.bind(this);
		this.handleAddNewObject = this.handleAddNewObject.bind(this);
		
		// Order changing methods:
		this.handleMoveUp = this.handleMoveUp.bind(this);
		this.handleMoveDown = this.handleMoveDown.bind(this);

		this._paramsMan = this.props.workspace.getParamsManager();
		this._fobjImageBank = this.props.workspace.getForegroundObjectsImageBank();

		this._paramsMan.registerCallback('change', this.paramsManagerChangeCallback);
		this._fobjImageBank.registerCallback('change', this.forceRender);
		this.registerObjectCallbacks(this._paramsMan.getForegroundObjects());

		this.state = {
			dirtyCount: 1,
			selectedImage: null,
			isAddNewDialogOpen: false,
		};

	}


	/*
	**	General Methods
	*/

	forceRender() {
		this.setState(state => ({dirtyCount: state.dirtyCount+1}));
	}

	paramsManagerChangeCallback(type, caller, data) {

		// Register for callbacks on new objects if foregroundObjects has changed.
		if (data.newValues.hasOwnProperty('foregroundObjects')) {
			this.registerObjectCallbacks(data.newValues.foregroundObjects);
		}

		// Re-render only if any of the properties of interest have changed.
		for (const key of this._propertiesOfInterest) {
			if (data.newValues.hasOwnProperty(key)) {
				this.forceRender();
				return;
			}
		}
	}

	objectManagerChangeCallback(type, caller, data) {
		// Re-render only if any of the properties of interest have changed -- for foreground objects.
		for (const key of this._objectPropertiesOfInterest) {
			if (data.newValues.hasOwnProperty(key)) {
				this.forceRender();
				return;
			}
		}
	}

	registerObjectCallbacks(foregroundObjects) {
		// This method will register for change callbacks on the given foreground objects,
		//	skipping objects that have already been done.
		// (Foreground objects will automatically unregister all callbacks when deleted.)
		for (const fobj of foregroundObjects) {
			if (!fobj.__FOL_hasRegistered) {
				fobj.registerCallback('change', this.objectManagerChangeCallback);
				fobj.__FOL_hasRegistered = true;
			}
		}
	}


	/*
	**	New Object Methods
	*/

	handleOpenNewObjectDialog() {
		this.setState({selectedImage: null, isAddNewDialogOpen: true});
	}

	handleCloseNewObjectDialog() {
		this.setState({isAddNewDialogOpen: false});
	}

	handleSelectImage(image) {
		this.setState({selectedImage: image});
	}
		
	handleAddNewObject() {
		this.setState({isAddNewDialogOpen: false});
		let image = this.state.selectedImage;
		if (image === null) {
			console.error('Can\'t add new object. There is no selected image.');
			return;
		}
		let fobj = this._paramsMan.addNewForegroundObjectWithImage(image);
		this.props.onSelect(fobj); 
	}


	/*
	**	Order Changing Methods
	*/

	handleMoveUp() {
		let fobj = this.props.selected;

		if (fobj === null) {
			console.error('Move up clicked when selected object should be null.');
			this.forceRender();
			return;
		}

		let fobjs = this._paramsMan.getForegroundObjects();
		let index = fobjs.indexOf(fobj);

		if (index < 0) {
			console.error('Move up clicked on an object that could not be found.');
			this.forceRender();
			return;
		}

		if (index === fobjs.length - 1) {
			console.error('Move up clicked on an object that is already at the top.');
			this.forceRender();
			return;
		}
	
		this._paramsMan.changeForegroundObjectIndex(fobj, index+1);
	}

	handleMoveDown() {
		let fobj = this.props.selected;

		if (fobj === null) {
			console.error('Move down clicked when selected object should be null.');
			this.forceRender();
			return;
		}

		let fobjs = this._paramsMan.getForegroundObjects();
		let index = fobjs.indexOf(fobj);

		if (index < 0) {
			console.error('Move down clicked on an object that could not be found.');
			this.forceRender();
			return;
		}

		if (index === 0) {
			console.error('Move down clicked on an object that is already at the bottom.');
			this.forceRender();
			return;
		}
	
		this._paramsMan.changeForegroundObjectIndex(fobj, index-1);
	}


	/*
	**	render
	*/

	render() {

		let fobjs = this._paramsMan.getForegroundObjects();

		let disableUp = true;
		let disableDown = true;

		let list = [];
		for (let i = 0; i < fobjs.length; ++i) {
			let fobj = fobjs[i];

			let isSelected = (this.props.selected === fobj);
			if (isSelected) {
				disableDown = (i <= 0);
				disableUp = (i >= fobjs.length - 1);
			}	
			list.unshift(<Button style={{display: "block"}} onClick={() => this.props.onSelect(fobj)} text={fobj.getName()}
										fill minimal alignment="left" active={isSelected} intent={isSelected ? "primary" : "none"}
										key={fobj.getId()} />);
		}

		return (
			<div style={{width: "250px", margin: "0", display: "inline-block", verticalAlign: 'top'}}>
				
				<Dialog style={{minWidth: "600px"}} isOpen={this.state.isAddNewDialogOpen} onClose={this.handleCloseNewObjectDialog}
						title="Select Image for Foreground Object">
					<div style={{padding: "30px"}}>
						<Button onClick={this.handleAddNewObject} intent="primary" disabled={this.state.selectedImage === null}
							style={{left: "50%", position: "relative", display: "block", transform: "translateX(-50%)"}} >
							Add New Foreground Object With Selected Image
						</Button>
						<ImagesList bank={this._fobjImageBank} onSelect={this.handleSelectImage} selected={this.state.selectedImage} />
					</div>
				</Dialog>

				<Button icon="add" onClick={this.handleOpenNewObjectDialog}>Add New</Button>
				<Button icon="arrow-up" disabled={disableUp} onClick={this.handleMoveUp} style={{float: "right"}} />
				<Button icon="arrow-down" disabled={disableDown} onClick={this.handleMoveDown} style={{float: "right", marginRight: "3px"}} />
			
				<div style={{width: "100%", border: "1px solid #e0e0e0", marginTop: "5px", padding: "5px"}}>
					{list}
				</div>

			</div>
		);
	}

}
	

